import { LinearProgress } from '@mui/material';
import { downloadCard } from './Card.style';
import EBOOK from '/assets/image/ebook.svg';
import PAPER from '/assets/image/paper.svg';
import { useCallback, useMemo } from 'react';
import { DEVICE, useDevice } from '~/utils/hook/useDevice';
import {
  DeleteRounded,
  PauseRounded,
  CloseRounded,
  PlayArrowRounded,
  FolderRounded,
} from '@mui/icons-material';
import {
  BaseButton,
  BASE_BUTTON_VARIANT,
  BASE_BUTTON_COLOR,
} from '~/components/Global/Button';
import { FileStatus, THistoryItem } from '~/store/resource/resource.type';
import { useDownload } from '~/utils/hook/useDownload';
import { sendChannel } from '~/utils/constants';
import { useAutoExtractEffect } from '~/utils/hook/useAutoExtractEffect';
import { getDirPath } from '~/utils/helper/function';
import { installableFileFormats } from '~/components/Global/Card/Card.constant';
import { useFile } from '~/utils/hook/useFile';

export type DownloadCardAction =
  | typeof sendChannel.pause
  | typeof sendChannel.resume
  | typeof sendChannel.cancel
  | typeof sendChannel.openPath
  | typeof sendChannel.deleteFile;

type TProp = {
  history: THistoryItem;
  onAction?: (action: DownloadCardAction) => void;
};

export const DownloadCard = ({ history, onAction }: TProp) => {
  const {
    progress,
    fileFormat,
    fileName,
    size,
    key,
    status,
    url,
    updateTime,
    totalBytes,
  } = history;
  const { device } = useDevice();
  const { pause, resume, cancel, deleteFile } = useDownload();
  const { openPath } = useFile();
  const isPause = useMemo(() => status === FileStatus.paused, [status]);

  const handleAction = useCallback(
    (action: DownloadCardAction) => {
      switch (action) {
        case sendChannel.deleteFile:
          deleteFile({ filePath: key, filename: fileName });
          break;
        case sendChannel.pause:
          pause(key);
          break;
        case sendChannel.resume:
          resume(key);
          break;
        case sendChannel.cancel:
          cancel(key);
          break;
        case sendChannel.openPath:
          openPath(getDirPath({ key, fileName }));
          break;
        default:
          break;
      }
      onAction?.(action);
    },
    [key, fileName, onAction],
  );

  useAutoExtractEffect({ status, key, fileName, url, updateTime, totalBytes });

  return (
    <downloadCard.Card>
      <downloadCard.Main>
        <downloadCard.Img
          src={installableFileFormats.includes(fileFormat) ? EBOOK : PAPER}
          alt={fileFormat}
        />
        <downloadCard.Info>
          <downloadCard.Title>{fileName}</downloadCard.Title>
          <downloadCard.Content isProgressing={status !== FileStatus.finish}>
            {size}
            {status !== FileStatus.finish && <span>{progress}%</span>}
          </downloadCard.Content>
          {status !== FileStatus.finish && (
            <LinearProgress variant="determinate" value={progress} />
          )}
        </downloadCard.Info>
      </downloadCard.Main>
      <downloadCard.Action>
        {(status === FileStatus.completed || status === FileStatus.finish) && (
          <>
            <BaseButton
              variant={BASE_BUTTON_VARIANT.FILLED}
              color={BASE_BUTTON_COLOR.PRIMARY}
              text="開啟位置"
              icon={
                device === DEVICE.MOBILE ? undefined : (
                  <FolderRounded sx={{ fill: 'white' }} />
                )
              }
              onClick={() => handleAction(sendChannel.openPath)}
            />
            <BaseButton
              variant={BASE_BUTTON_VARIANT.OUTLINED}
              color={BASE_BUTTON_COLOR.PRIMARY}
              text="刪除"
              icon={
                device === DEVICE.MOBILE ? undefined : (
                  <DeleteRounded fill="#5F73BC" sx={{ fontSize: '32px' }} />
                )
              }
              onClick={() => handleAction(sendChannel.deleteFile)}
            />
          </>
        )}
        {(status === FileStatus.progressing ||
          status === FileStatus.paused ||
          status === FileStatus.installing) && (
          <>
            {status !== FileStatus.installing && (
              <BaseButton
                variant={
                  isPause
                    ? BASE_BUTTON_VARIANT.FILLED
                    : BASE_BUTTON_VARIANT.OUTLINED
                }
                color={BASE_BUTTON_COLOR.DOWNLOAD}
                text={isPause ? '繼續' : '暫停'}
                icon={
                  device === DEVICE.MOBILE ? undefined : isPause ? (
                    <PlayArrowRounded
                      sx={{ fontSize: '26px', fill: 'white' }}
                    />
                  ) : (
                    <PauseRounded
                      sx={{
                        fill: 'secondary.dark',
                        stroke: 'secondary.dark',
                        strokeWidth: '3',
                      }}
                    />
                  )
                }
                onClick={() =>
                  isPause
                    ? handleAction(sendChannel.resume)
                    : handleAction(sendChannel.pause)
                }
              />
            )}
            <BaseButton
              variant={BASE_BUTTON_VARIANT.OUTLINED}
              color={BASE_BUTTON_COLOR.PRIMARY}
              text="取消"
              icon={
                device === DEVICE.MOBILE ? undefined : (
                  <CloseRounded
                    sx={{
                      stroke: 'secondary.darker',
                      strokeWidth: '3',
                    }}
                  />
                )
              }
              onClick={() => handleAction(sendChannel.cancel)}
            />
          </>
        )}
      </downloadCard.Action>
    </downloadCard.Card>
  );
};
